import { pageSize } from "../../api/apiUtils";

export type OrganizationSearchQueryType = {
  org_name?: string;
  country_id?: string;
  parent_organization_id?: number;
  address?: string;
  comments?: string;
  website?: string;
  primary_contact_email?: string;
  primary_contact_phone?: string;
  ml_ops_id?: number;
  inherit_permissions?: boolean;
  skip: number;
  take?: number;
  sort_by?: string;
  sort_direction?: string;
  all_orgs?: boolean;
};

export type ProjectSearchQueryType = {
  project_name?: string;
  project_id?: number;
  primary_contact_email?: string;
  org_id?: number;
  country_id?: number;
  skip: number;
  take?: number;
  sort_by?: string;
  sort_direction?: string;
  all_projects?: boolean;
  permission?: string;
};

export type OrgUserSearchQueryType = {
  org_id: number;
  is_verified?: boolean;
  first_name?: string;
  last_name?: string;
  email?: string;
  role_id?: number;
  take?: number;
  skip?: number;
  all_users?: boolean;
};

export type ProjectUserSearchQueryType = {
  proj_id: number;
  is_verified?: boolean;
  first_name?: string;
  last_name?: string;
  email?: string;
  role_id?: number;
  take?: number;
  skip?: number;
  all_users?: boolean;
};

export type DeploymentSearchQueryType = {
  project_id: number;
  deployment_name?: string;
  location_id?: number;
  location_name?: string;
  sensor_id?: number;
  sensor_name?: string;
  // Explicitly nullable (not just optional) — the search bar sends `null`
  // rather than an empty string when a date field is cleared, so an unset
  // bound is genuinely absent from the request (see getQueryString, which
  // only omits `null` values) instead of an empty `time_range_end=` that a
  // date-range filter can't meaningfully parse.
  time_range_start?: string | null;
  time_range_end?: string | null;
  sort_by?: string;
  sort_direction?: string;
  take?: number;
  skip?: number;
  all_deployments?: boolean;
};

export type SetSecretQueryType = {
  project_id: number;
  secret_name: string;
  secret_value: string;
};

export type GetSecretQueryType = {
  project_id: number;
  secret_name: string;
};

export type LocationSearchQueryType = {
  project_id: number;
  location_name?: string;
  country_id?: string;
  sort_by?: string;
  sort_direction?: string;
  take?: number;
  skip?: number;
  all_locations?: boolean;
};

export type SensorSearchQueryType = {
  sensor_name?: string;
  org_id?: number;
  project_id?: number;
  org_name?: string;
  sensor_type?: string;
  make?: string;
  model?: string;
  serial_number?: string;
  // See DeploymentSearchQueryType.time_range_start for why these are
  // nullable, not just optional.
  purchase_date_start?: string | null;
  purchase_date_end?: string | null;
  active_only?: boolean;
  sort_by?: string;
  sort_direction?: string;
  take?: number;
  skip?: number;
  all_sensors?: boolean;
};

export type ModelSearchQueryType = {
  model_id?: number;
  model_display_name?: string;
  detection_model?: string;
  classification_model?: string;
  skip?: number;
  take?: number;
  sort_by?: string;
  sort_direction?: string;
};

// --- Media query endpoint (AKL-680) ---------------------------------------
// Request shape for POST /media-assets/query. Field names are snake_case
// because this object is serialized straight into the request body.

export type ReviewStatusFilter = "unreviewed" | "reviewed";
export type ObservationTypeFilter =
  | "animal"
  | "human"
  | "vehicle"
  | "blank"
  | "unknown";
export type InferenceStateFilter =
  | "no_run"
  | "in_progress"
  | "completed"
  | "failed";
export type MediaSortKey =
  | "capture_time_asc"
  | "capture_time_desc"
  | "confidence_desc"
  | "random";

export type MediaAssetQueryFilters = {
  deployment_ids?: number[];
  location_ids?: number[];
  sensor_ids?: number[];
  capture_time_from?: string;
  capture_time_to?: string;
  review_status?: ReviewStatusFilter;
  predicted_taxon_ids?: number[];
  confidence_min?: number;
  confidence_max?: number;
  observation_type?: ObservationTypeFilter;
  inference_state?: InferenceStateFilter;
};

export type MediaAssetQuerySort = {
  key: MediaSortKey;
  seed?: number | null;
};

export type MediaAssetQueryType = {
  project_id: number;
  filters: MediaAssetQueryFilters;
  sort: MediaAssetQuerySort;
  skip: number;
  limit?: number | null;
};

// --- Media detail endpoint (AKL-681) --------------------------------------
// Request shape for POST /media-assets/detail. The optional navigation context
// reuses the Slice-4 filter/sort shapes so server-computed prev/next stays
// consistent with the feed ordering; when omitted, the response navigation is
// null and the web navigates from its in-memory feed list.

export type MediaDetailNavigationContext = {
  filters: MediaAssetQueryFilters;
  sort: MediaAssetQuerySort;
};

export type MediaDetailRequestType = {
  media_id: number;
  navigation?: MediaDetailNavigationContext | null;
};

// --- Review action endpoints (AKL-682, Photo Review Phase 2 Slice 6) --------
// Request shapes for the commit-action endpoints under `/review/*` (mounted at
// that prefix on the API). All snake_case (serialized straight into the request
// body); `media_id` travels in the body. Every commit may carry an optional
// `idempotency_key` so a double-click or retry re-reads state instead of
// double-writing. Actions anchor to explicit `prediction_ids` (from the detail
// payload's `underlying_prediction_ids`) — never an aggregation key — and
// `applied_at` selects aggregate (N predictions) vs object (single bbox)
// precision, matching the resulting observation level (Q2). The web never sends
// `observation_type` (derived server-side from the taxon's WI category) nor
// `source_prediction_id` (recovered via PredictionReview).

/** Aggregate (card) vs object (single bbox) action precision (§5, Q2). */
export type PredictionAppliedAt = "aggregate" | "object";

/** Accept N predictions as taxon T (§5.1.1 aggregate / §5.2.3 object). */
export type AcceptSuggestionRequest = {
  media_id: number;
  prediction_ids: number[];
  taxon_id: number;
  applied_at: PredictionAppliedAt;
  // Object actions: the resolved taxon of the parent aggregate card, so its
  // media-level observation count can be decremented server-side.
  parent_taxon_id?: number;
  idempotency_key?: string;
};

/** Reject N predictions (§5.1.2 aggregate / §5.2.1 object). */
export type RejectSuggestionRequest = {
  media_id: number;
  prediction_ids: number[];
  applied_at: PredictionAppliedAt;
  // Aggregate reject: the card's own taxon (so a re-action soft-deletes its
  // prior observation). Object reject: the parent card's taxon (to decrement).
  parent_taxon_id?: number;
  idempotency_key?: string;
};

/**
 * Edit N predictions (§5.1.3 aggregate / §5.2.2 object): change taxon and/or
 * count and/or comments. `original_taxon_id` is the card's current resolved
 * taxon; `new_*` fields are sent only when they change. The API requires a real
 * change (taxon, count, or comments).
 *
 * AKL-717: `original_taxon_id` is null for an object edit of an `unresolved`
 * box — a detection with no species to start from. The API accepts that for
 * object scope only, and requires `new_taxon_id` alongside it.
 */
export type EditSuggestionRequest = {
  media_id: number;
  prediction_ids: number[];
  original_taxon_id: number | null;
  new_taxon_id?: number;
  new_count?: number;
  comments?: string | null;
  applied_at: PredictionAppliedAt;
  parent_taxon_id?: number;
  idempotency_key?: string;
};

/** One ticked suggestion card in an assign-species request (AKL-774). */
export type AssignSpeciesCardRequest = {
  prediction_ids: number[];
  /**
   * The taxon the card currently resolves to. Sent per card because the server
   * decides accept-vs-edit from it rather than re-resolving the mapping - the
   * action must commit what the reviewer was looking at.
   */
  original_taxon_id: number;
};

/**
 * Point several suggestion cards at one species within a photo, committing them
 * as a single observation covering every box in those cards (AKL-774).
 *
 * `individual_count` carries whatever the editor holds, including the prefilled
 * value when the reviewer leaves it alone. It is omitted only when the field has
 * been cleared, which the server reads as "use the distinct-box count" — the
 * same number the editor prefilled.
 */
export type AssignSpeciesRequest = {
  media_id: number;
  cards: AssignSpeciesCardRequest[];
  taxon_id: number;
  individual_count?: number | null;
  comments?: string | null;
  idempotency_key?: string;
};

/**
 * Accept several ticked suggestion cards, each at its **own** species (AKL-779).
 *
 * The deliberate opposite of `AssignSpeciesRequest`: three ticked cards produce
 * three observations, one per card at the species that card already resolves to,
 * where assign-species produces one observation across all of them. That is why
 * there is no top-level `taxon_id` or `individual_count` — each card supplies
 * its own taxon via `original_taxon_id`, and each observation counts its own
 * boxes.
 */
export type AcceptSelectedCardsRequest = {
  media_id: number;
  cards: AssignSpeciesCardRequest[];
  idempotency_key?: string;
};

/**
 * Reject every ticked suggestion cluster in one request (AKL-779). Creates no
 * observations; each card's `original_taxon_id` is used server-side only as the
 * fallback for reconciling a legacy observation with no stored cluster.
 */
export type RejectSelectedCardsRequest = {
  media_id: number;
  cards: AssignSpeciesCardRequest[];
  idempotency_key?: string;
};

/** Create a manual observation (§5.3.1). observation_type is server-derived. */
export type ObservationCreateRequest = {
  media_id: number;
  taxon_id: number;
  individual_count?: number | null;
  comments?: string | null;
  idempotency_key?: string;
};

/** Edit an existing observation in place (§5.3.2). */
export type ObservationUpdateRequest = {
  taxon_id?: number;
  individual_count?: number | null;
  comments?: string | null;
};

/** Mark a photo blank with rejection fan-out over unactioned predictions (§5.3.3). */
export type MarkBlankRequest = {
  media_id: number;
  idempotency_key?: string;
};

/** Commit the photo as reviewed; 422 when zero observations exist (§5.4). */
export type SaveMediaRequest = {
  media_id: number;
  idempotency_key?: string;
};

export const getDefaultSensorsSearchQuery = (
  orgId: number,
): SensorSearchQueryType => ({
  org_id: orgId,
  active_only: true,
  skip: 0,
  take: pageSize,
  sort_by: "sensor_name",
  sort_direction: "asc",
});
